import React, { useEffect, useMemo, useState } from 'react';
import baseEntitlements from '../../data/entitlements.json';
import { getUserRoleFromToken } from '../../utils/jwt';
import {
  EMPTY_BILLING_DEFAULTS,
  PACKAGE_DISPLAY_ORDER,
  PACKAGE_FEATURE_LABELS,
  PACKAGE_FEATURE_ORDER,
  formatVnd,
  isPlainObject,
  summarizeIncludedFeatures,
} from '../../utils/packageCatalog';

const RUNTIME_GROUPS = [
  {
    key: 'core',
    title: 'Device Limits',
    description: 'These values already affect live device-limit checks and limit snapshots.',
    rows: [
      { feature: 'tv_screens_included', label: 'TV screens included', type: 'number', wired: true },
      { feature: 'music_players', label: 'Music players', type: 'number', wired: true },
      { feature: 'console_tablets', label: 'Console remotes', type: 'text', wired: true },
      { feature: 'staff_phones', label: 'Phone remotes', type: 'text', wired: true },
      { feature: 'tv_screen_overage_allowed', label: 'Extra TV screens allowed', type: 'boolean' },
      { feature: 'tv_screen_overage_price', label: 'Extra TV price', type: 'number', format: 'currency' },
    ],
  },
  {
    key: 'generative_ai',
    title: 'AI Usage',
    description: 'These values already drive monthly/lifetime AI credit enforcement.',
    rows: [
      { feature: 'ai_credits_monthly', label: 'AI credits monthly', type: 'number', wired: true },
      { feature: 'ai_credits_lifetime', label: 'AI credits lifetime', type: 'number', wired: true },
      { feature: 'ai_hard_stop', label: 'AI hard stop', type: 'boolean', wired: true },
      { feature: 'ai_bundle_purchase', label: 'AI bundle purchase allowed', type: 'boolean' },
      { feature: 'ai_bundle_expiry_days', label: 'AI bundle expiry days', type: 'number' },
      { feature: 'ai_tools_level', label: 'AI tools level', type: 'text' },
    ],
  },
  {
    key: 'content_storage',
    title: 'Playback & Storage',
    description: 'These are product-facing package settings already shown in previews and some playback flows.',
    rows: [
      { feature: 'video_resolution_cap', label: 'Video resolution cap', type: 'text' },
      { feature: 'video_upload_duration_limit', label: 'Video upload duration', type: 'text' },
      { feature: 'image_uploads', label: 'Image uploads', type: 'text' },
      { feature: 'continuous_loop_without_ads', label: 'Continuous loop without ads', type: 'text' },
      { feature: 's3_soft_limit', label: 'S3 soft limit', type: 'text' },
    ],
  },
  {
    key: 'control',
    title: 'Control & Hardware',
    description: 'These settings are already consulted by parts of the live app.',
    rows: [
      { feature: 'google_assistant_control', label: 'Google Assistant control', type: 'boolean' },
      { feature: 'hardware_control_allowed', label: 'Hardware control allowed', type: 'boolean' },
      { feature: 'hardware_required', label: 'Hardware required', type: 'boolean' },
      { feature: 'virtual_matrix_features', label: 'Virtual matrix features', type: 'boolean' },
      { feature: 'stage_mode', label: 'Stage mode', type: 'text', wired: true },
    ],
  },
  {
    key: 'analytics',
    title: 'Analytics & Pixels',
    description: 'These flags already gate the custom analytics settings in the app.',
    rows: [
      { feature: 'custom_ga4_enabled', label: 'Custom GA4', type: 'boolean', wired: true },
      { feature: 'custom_fb_pixel_enabled', label: 'Custom Facebook Pixel', type: 'boolean', wired: true },
      { feature: 'custom_clarity_enabled', label: 'Custom Clarity', type: 'boolean', wired: true },
      { feature: 'custom_tiktok_pixel_enabled', label: 'Custom TikTok Pixel', type: 'boolean', wired: true },
    ],
  },
];

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const isTierMap = (value, tiers) => {
  if (!isPlainObject(value)) return false;
  if (!Array.isArray(tiers) || tiers.length === 0) return false;
  return tiers.some((tier) => Object.prototype.hasOwnProperty.call(value, tier));
};

const ensureTiersPresent = (entitlements, desiredTiers) => {
  const next = deepClone(entitlements || {});
  const tiers = Array.isArray(desiredTiers) ? desiredTiers : [];
  next.tiers = tiers;

  Object.entries(next).forEach(([sectionKey, section]) => {
    if (sectionKey === 'tiers') return;
    if (!isPlainObject(section)) return;

    Object.entries(section).forEach(([, tierMap]) => {
      if (!isTierMap(tierMap, tiers)) return;
      tiers.forEach((tier) => {
        if (Object.prototype.hasOwnProperty.call(tierMap, tier)) return;
        const fallbackTier = tier === 'BASIC' && Object.prototype.hasOwnProperty.call(tierMap, 'STANDARD')
          ? 'STANDARD'
          : tiers.find((candidateTier) => Object.prototype.hasOwnProperty.call(tierMap, candidateTier));
        tierMap[tier] = fallbackTier ? tierMap[fallbackTier] : null;
      });
    });
  });

  return next;
};

const formatPackagePrice = (value) => {
  if (value == null) return 'POA';
  return `${formatVnd(value)} VND / month`;
};

const formatInputValue = (value, row) => {
  if (row?.format === 'currency' && value != null && value !== '') {
    const amount = Number(value);
    if (Number.isFinite(amount)) return `${formatVnd(amount)} VND`;
  }
  if (value === null || typeof value === 'undefined' || value === '') return '—';
  return String(value);
};

const buildTierUpdates = (original, draft, tiers) => {
  const updatesByTier = tiers.reduce((acc, tier) => {
    acc[tier] = {};
    return acc;
  }, {});

  const originalObj = original || {};
  const draftObj = draft || {};

  Object.entries(draftObj).forEach(([sectionKey, section]) => {
    if (sectionKey === 'tiers') return;
    if (!isPlainObject(section)) return;

    Object.entries(section).forEach(([featureKey, tierMap]) => {
      if (!isTierMap(tierMap, tiers)) return;

      tiers.forEach((tier) => {
        const originalValue = originalObj?.[sectionKey]?.[featureKey]?.[tier];
        const nextValue = draftObj?.[sectionKey]?.[featureKey]?.[tier];
        if (JSON.stringify(originalValue) !== JSON.stringify(nextValue)) {
          updatesByTier[tier][featureKey] = nextValue;
        }
      });
    });
  });

  return updatesByTier;
};

const parseTierValue = (rawValue, type) => {
  if (type === 'number') {
    if (rawValue === '') return null;
    const numeric = Number(rawValue);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return rawValue === '' ? null : rawValue;
};

function Subscriptions() {
  const [originalEntitlements, setOriginalEntitlements] = useState(() => deepClone(baseEntitlements));
  const [draftEntitlements, setDraftEntitlements] = useState(() => deepClone(baseEntitlements));
  const [packageCatalog, setPackageCatalog] = useState({});
  const [billingDefaults, setBillingDefaults] = useState(EMPTY_BILLING_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState('');
  const [serverMessage, setServerMessage] = useState('');
  const [isSavingServer, setIsSavingServer] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState({
    commercialPackages: false,
    globalAddOns: false,
    runtimeEntitlements: false,
  });

  const isAdmin = useMemo(() => {
    return getUserRoleFromToken(localStorage.getItem('token')) === 'admin';
  }, []);

  const tiers = useMemo(() => {
    const candidate = draftEntitlements?.tiers;
    return Array.isArray(candidate) && candidate.length ? candidate : ['FREE', 'BASIC', 'STANDARD', 'PREMIUM', 'ENTERPRISE'];
  }, [draftEntitlements]);

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setServerError('');
      setServerMessage('');

      if (!isAdmin) {
        setServerError('Forbidden: admin only');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/admin/entitlements', {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(json?.message || `Failed to load subscriptions (${res.status})`);
        }

        if (ignore) return;

        const entitlementsPayload = json?.entitlements || json;
        const serverTiers = Array.isArray(entitlementsPayload?.tiers) && entitlementsPayload.tiers.length
          ? entitlementsPayload.tiers
          : ['FREE', 'BASIC', 'STANDARD', 'PREMIUM', 'ENTERPRISE'];
        const normalizedEntitlements = ensureTiersPresent(entitlementsPayload, serverTiers);

        setOriginalEntitlements(deepClone(normalizedEntitlements));
        setDraftEntitlements(deepClone(normalizedEntitlements));
        setPackageCatalog(isPlainObject(json?.packageCatalog) ? json.packageCatalog : {});
        setBillingDefaults(isPlainObject(json?.billingDefaults) ? json.billingDefaults : EMPTY_BILLING_DEFAULTS);
      } catch (error) {
        if (!ignore) {
          setOriginalEntitlements(deepClone(baseEntitlements));
          setDraftEntitlements(deepClone(baseEntitlements));
          setPackageCatalog({});
          setBillingDefaults(EMPTY_BILLING_DEFAULTS);
          setServerError(error.message || 'Failed to load subscriptions.');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [isAdmin]);

  const updateTierValue = (sectionKey, featureKey, tierKey, nextValue) => {
    setDraftEntitlements((prev) => {
      const next = deepClone(prev);
      if (!next[sectionKey]) next[sectionKey] = {};
      if (!next[sectionKey][featureKey]) next[sectionKey][featureKey] = {};
      next[sectionKey][featureKey][tierKey] = nextValue;
      return next;
    });
  };

  const resetDraft = () => {
    setDraftEntitlements(deepClone(originalEntitlements));
    setServerError('');
    setServerMessage('');
  };

  const saveToServer = async () => {
    if (!isAdmin) {
      setServerError('Forbidden: admin only');
      return;
    }

    setIsSavingServer(true);
    setServerError('');
    setServerMessage('');

    try {
      const token = localStorage.getItem('token');
      const updatesByTier = buildTierUpdates(originalEntitlements, draftEntitlements, tiers);

      const responses = await Promise.all(
        tiers.map(async (tier) => {
          const updates = updatesByTier[tier] || {};
          if (!Object.keys(updates).length) return { tier, unknownKeys: [] };

          const res = await fetch(`/api/admin/entitlements/${encodeURIComponent(tier)}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updates),
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(body?.message || `Failed to save ${tier} (${res.status})`);
          return { tier, unknownKeys: body?.unknownKeys || [] };
        })
      );

      const unknown = responses.flatMap((response) => response.unknownKeys || []);
      setOriginalEntitlements(deepClone(draftEntitlements));
      setServerMessage(
        unknown.length
          ? `Saved runtime entitlements. Ignored unknown keys: ${[...new Set(unknown)].join(', ')}`
          : 'Saved runtime entitlements.'
      );
    } catch (error) {
      setServerError(error.message || 'Failed to save runtime entitlements.');
    } finally {
      setIsSavingServer(false);
    }
  };

  const packageCards = useMemo(() => {
    const source = isPlainObject(packageCatalog) ? packageCatalog : {};
    return PACKAGE_DISPLAY_ORDER
      .map((key) => source[key])
      .filter(Boolean);
  }, [packageCatalog]);

  const globalAddOnRows = useMemo(() => {
    return PACKAGE_FEATURE_ORDER.map((key) => ({
      key,
      label: PACKAGE_FEATURE_LABELS[key],
      price: Number(billingDefaults?.[key] || 0),
    }));
  }, [billingDefaults]);

  const runtimeTierBadges = useMemo(() => {
    return tiers.map((tier) => ({
      key: tier,
      label: tier,
      commercialLabel: packageCatalog?.[String(tier || '').toLowerCase()]?.label || tier,
    }));
  }, [packageCatalog, tiers]);

  const runtimeWiredCounts = useMemo(() => {
    const total = RUNTIME_GROUPS.reduce((sum, group) => sum + group.rows.length, 0);
    const wired = RUNTIME_GROUPS.reduce(
      (sum, group) => sum + group.rows.filter((row) => row.wired).length,
      0
    );
    return { total, wired };
  }, []);

  const togglePanel = (panelKey) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  };

  const renderExpandablePanel = ({
    panelKey,
    title,
    summary,
    children,
  }) => {
    const expanded = Boolean(expandedPanels[panelKey]);
    return (
      <div className="rounded-xl border border-slate-700 bg-slate-900/40">
        <button
          type="button"
          onClick={() => togglePanel(panelKey)}
          className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        >
          <div>
            <h2 className="text-xl font-semibold text-slate-100">{title}</h2>
            <p className="mt-1 text-sm text-slate-400">{summary}</p>
          </div>
          <span className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
            {expanded ? 'Collapse' : 'Expand'}
          </span>
        </button>
        {expanded && (
          <div className="border-t border-slate-800 px-5 py-5">
            {children}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4 text-sm text-slate-300">
        Loading subscriptions…
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Subscriptions</h1>
          <p className="mt-1 text-sm text-slate-400">
            Commercial package catalog plus the runtime entitlement rows that already affect live behavior.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={saveToServer}
            disabled={isSavingServer}
          >
            {isSavingServer ? 'Saving…' : 'Save Runtime Controls'}
          </button>
          <button
            className="rounded bg-slate-800 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-700"
            onClick={resetDraft}
          >
            Reset
          </button>
        </div>
      </div>

      {serverError && (
        <div className="mb-4 rounded border border-rose-700 bg-rose-950/30 p-3 text-sm text-rose-200">
          {serverError}
        </div>
      )}
      {serverMessage && (
        <div className="mb-4 rounded border border-emerald-700 bg-emerald-950/30 p-3 text-sm text-emerald-200">
          {serverMessage}
        </div>
      )}

      <div className="space-y-4">
        {renderExpandablePanel({
          panelKey: 'commercialPackages',
          title: 'Commercial Packages',
          summary: `${packageCards.length} packages in VND. Shared commercial language for Billing, onboarding, and future public pricing.`,
          children: (
            <>
              <div className="mb-4 flex justify-end">
                <div className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                  VND
                </div>
              </div>
              <div className="grid gap-4 xl:grid-cols-2">
                {packageCards.map((pkg) => {
                  const includedSummary = summarizeIncludedFeatures(pkg);

                  return (
                    <div
                      key={pkg.key}
                      className="rounded-2xl border border-slate-700 bg-slate-950/50 p-5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            {pkg.label}
                          </div>
                          <div className="mt-2 text-2xl font-bold text-slate-100">
                            {formatPackagePrice(pkg.basePrice)}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${pkg.advertisingEnabled ? 'bg-amber-500/15 text-amber-200' : 'bg-emerald-500/15 text-emerald-200'}`}>
                            {pkg.advertisingEnabled ? 'Ads On' : 'Ads Off'}
                          </span>
                          {pkg.maxSitesPerAccount === 1 && (
                            <span className="rounded-full bg-sky-500/15 px-3 py-1 text-xs font-semibold text-sky-200">
                              Single-site only
                            </span>
                          )}
                          {pkg.isPoa && (
                            <span className="rounded-full bg-violet-500/15 px-3 py-1 text-xs font-semibold text-violet-200">
                              Commercial only for now
                            </span>
                          )}
                        </div>
                      </div>

                      <p className="mt-4 text-sm text-slate-300">{pkg.summary}</p>

                      <div className="mt-4 grid gap-2 text-sm text-slate-300">
                        <div><span className="font-semibold text-slate-100">Website:</span> {pkg.websitePolicy}</div>
                        <div><span className="font-semibold text-slate-100">AI:</span> {pkg.aiPolicy}</div>
                        <div><span className="font-semibold text-slate-100">API:</span> {pkg.apiPolicy}</div>
                      </div>

                      <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                          Included Per Site
                        </div>
                        <div className="mt-2 text-sm text-slate-200">
                          {includedSummary || 'No included items defined'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ),
        })}

        {renderExpandablePanel({
          panelKey: 'globalAddOns',
          title: 'Global Add-On Pricing',
          summary: `${globalAddOnRows.length} overage prices used by the billing workbench.`,
          children: (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {globalAddOnRows.map((row) => (
                <div
                  key={`addon-${row.key}`}
                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3"
                >
                  <div className="text-sm text-slate-300">{row.label}</div>
                  <div className="mt-2 text-base font-semibold text-slate-100">{formatVnd(row.price)} VND</div>
                </div>
              ))}
            </div>
          ),
        })}

        {renderExpandablePanel({
          panelKey: 'runtimeEntitlements',
          title: 'Runtime Entitlements',
          summary: `${runtimeWiredCounts.wired} of ${runtimeWiredCounts.total} rows are live today. Green rows are already wired through.`,
          children: (
            <>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-200">
                  Green = live today
                </div>
                <div className="flex flex-wrap gap-2">
                  {runtimeTierBadges.map((tier) => (
                    <span
                      key={`tier-badge-${tier.key}`}
                      className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-200"
                    >
                      {tier.commercialLabel}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {RUNTIME_GROUPS.map((group) => (
                  <div
                    key={group.key}
                    className="rounded-2xl border border-slate-700 bg-slate-950/50 p-4"
                  >
                    <div className="mb-3">
                      <h3 className="text-base font-semibold text-slate-100">{group.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{group.description}</p>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-800 text-left text-slate-300">
                            <th className="px-3 py-2 font-medium">Setting</th>
                            {tiers.map((tier) => (
                              <th key={`${group.key}:${tier}`} className="px-3 py-2 font-medium">
                                {packageCatalog?.[String(tier || '').toLowerCase()]?.label || tier}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                          {group.rows.map((row) => (
                            <tr key={`${group.key}:${row.feature}`} className="align-top">
                              <td className="px-3 py-3">
                                <div className="flex items-center gap-2">
                                  <span className={row.wired ? 'font-medium text-emerald-300' : 'text-slate-200'}>
                                    {row.label}
                                  </span>
                                  {row.wired && (
                                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-200">
                                      Live
                                    </span>
                                  )}
                                </div>
                              </td>
                              {tiers.map((tier) => {
                                const value = draftEntitlements?.[group.key]?.[row.feature]?.[tier];

                                if (row.type === 'boolean') {
                                  return (
                                    <td key={`${group.key}:${row.feature}:${tier}`} className="px-3 py-3">
                                      <label className="inline-flex items-center gap-2 text-slate-200">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(value)}
                                          onChange={(e) => updateTierValue(group.key, row.feature, tier, e.target.checked)}
                                        />
                                        <span className="text-xs text-slate-400">
                                          {Boolean(value) ? 'Enabled' : 'Disabled'}
                                        </span>
                                      </label>
                                    </td>
                                  );
                                }

                                return (
                                  <td key={`${group.key}:${row.feature}:${tier}`} className="px-3 py-3">
                                    <input
                                      type={row.type === 'number' ? 'number' : 'text'}
                                      className="w-44 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                                      value={value === null || typeof value === 'undefined' ? '' : String(value)}
                                      onChange={(e) => updateTierValue(
                                        group.key,
                                        row.feature,
                                        tier,
                                        parseTierValue(e.target.value, row.type)
                                      )}
                                      placeholder={formatInputValue(value, row)}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ),
        })}
      </div>
    </div>
  );
}

export default Subscriptions;
